import React, { useMemo } from 'react';

const OPERATOR_LABELS = {
  '==': 'equals', '!=': '≠', '>': '>', '>=': '≥', '<': '<', '<=': '≤',
  'contains': 'contains', 'not_contains': "doesn't contain",
  'in': 'is in', 'not_in': 'not in',
  'exists': 'is set', 'not_exists': 'not set',
  'is_true': 'is true', 'is_false': 'is false',
};

const ICON_BY_TYPE = {
  debug: '🐞', openai_agent: '🤖', gemini_agent: '✨', anthropic: '🧠',
  ollama: '🦙', http_request: '🌐', edit_fields: '✏️', webhook: '🔗',
  webhook_response: '📤', if: '❓', switch: '🔀', loop: '🔄',
  try_catch: '🛡', wait: '⏳', merge: '⛙', code: '💻', chat_trigger: '💬',
  gmail_trigger: '📨', outlook_trigger: '📧', form_trigger: '📋',
  google_sheets_trigger: '📊', airtable_trigger: '🗂', github_trigger: '🐙',
  slack_trigger: '💬', jira_trigger: '🎯', notion_trigger: '📓',
  rss_feed_trigger: '📡', human_approval: '✅', form_input: '📝',
  set_variables: '📌', stop_and_error: '🛑', parallel_branches: '⑂',
  call_workflow: '▶', datastore: '🗃️', table: '📋', table_trigger: '📋',
  postgres_query: '🐘', mysql_query: '🐬', sql_server_query: '🗄️',
  mongodb_query: '🍃', slack: '#️⃣', teams: '💬', discord: '🎮',
  gmail: '📨', outlook: '📧', send_mail: '📧', google_sheets: '📊',
  google_docs: '📝', excel: '📗', notion: '📓', jira: '🎫',
  confluence: '🔷', trello: '🃏', asana: '✅', aws_s3: '🪣',
  stripe: '💳', hubspot: '🟠', salesforce: '☁️', zendesk: '🎧',
  text_manipulator: '🔤', json_processor: '{ }', csv_processor: '📄',
  datetime_formatter: '📅', data_validation: '✔️', aggregate: '∑',
  sort: '⇅', deduplicate: '🔂', split_in_batches: '📦',
  compare_datasets: '⇄', vector_store: '📚', embeddings: '🧠',
};

function getIcon(node) {
  const type = node.data?.type || node.type;
  return node.data?.icon || ICON_BY_TYPE[type] || '⬡';
}

function getNodeTypeLabel(node) {
  const type = node.data?.type || node.type;
  return type ? type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Node';
}

function summariseParams(node) {
  const type = node.data?.type || node.type;
  const params = node.data?.params || {};

  if (type === 'if') {
    const conditions = params.conditions || [];
    const logic = params.logic === 'any' ? 'OR' : 'AND';
    if (!conditions.length) return 'No conditions configured';
    const parts = conditions.slice(0, 2).map(c => {
      const op = OPERATOR_LABELS[c.operator] || c.operator || '?';
      const needsRight = !['exists', 'not_exists', 'is_true', 'is_false'].includes(c.operator);
      return needsRight ? `${c.left || '…'} ${op} ${c.right ?? '…'}` : `${c.left || '…'} ${op}`;
    });
    const more = conditions.length > 2 ? ` + ${conditions.length - 2} more` : '';
    return `${parts.join(` ${logic} `)}${more}`;
  }
  if (type === 'switch') {
    const cases = params.cases || [];
    return cases.length ? `Cases: ${cases.slice(0, 3).join(', ')}${cases.length > 3 ? '…' : ''}` : 'No cases';
  }
  if (type === 'webhook') return params.slug ? `/${params.slug}` : 'No slug';
  if (type === 'http_request') return `${params.method || 'GET'} ${params.url || ''}`.trim();
  if (type === 'wait') return params.duration ? `Wait ${params.duration}s` : '';
  if (type === 'code') return params.language ? `Language: ${params.language}` : '';
  if (type === 'send_mail') return params.recipients ? `To: ${params.recipients}` : '';
  if (type === 'slack') return params.channel_id ? `#${params.channel_id}` : '';
  if (type === 'loop') return params.items ? `Items: ${String(params.items).slice(0, 40)}` : '';
  if (type === 'set_variables') {
    const vars = params.variables || [];
    return vars.length ? `Set: ${vars.slice(0, 3).map(v => v.name).join(', ')}` : '';
  }
  if (type === 'stop_and_error') return params.message ? `"${String(params.message).slice(0, 40)}"` : '';
  if (type === 'call_workflow') return params.workflow_id ? `Workflow: ${params.workflow_id}` : '';
  if (type === 'parallel_branches') {
    const branches = params.branch_names || [];
    return branches.length ? branches.join(', ') : '';
  }
  // Generic: show first non-empty param
  const keys = Object.keys(params).filter(k => k !== 'credential_id');
  for (const k of keys) {
    const v = params[k];
    if (v && typeof v === 'string') return `${k}: ${v.slice(0, 50)}`;
    if (v && typeof v === 'number') return `${k}: ${v}`;
  }
  return '';
}

/** Topological BFS from root nodes to get a linear display order */
function topoOrder(nodes, edges) {
  const visible = nodes.filter(n => n.type !== 'sticky_note' && n.type !== 'group' && !(n.hidden));
  const ids = new Set(visible.map(n => n.id));
  const childMap = {};
  const inDegree = {};
  for (const n of visible) inDegree[n.id] = 0;
  for (const e of edges) {
    if (!ids.has(e.source) || !ids.has(e.target)) continue;
    if (!childMap[e.source]) childMap[e.source] = [];
    childMap[e.source].push(e.target);
    inDegree[e.target] = (inDegree[e.target] || 0) + 1;
  }
  const queue = visible.filter(n => (inDegree[n.id] || 0) === 0);
  const visited = new Set();
  const result = [];
  while (queue.length) {
    const node = queue.shift();
    if (visited.has(node.id)) continue;
    visited.add(node.id);
    result.push(node);
    for (const childId of (childMap[node.id] || [])) {
      if (!visited.has(childId)) {
        const child = visible.find(n => n.id === childId);
        if (child) queue.push(child);
      }
    }
  }
  // append any orphans
  for (const n of visible) {
    if (!visited.has(n.id)) result.push(n);
  }
  return result;
}

const BRANCHING_TYPES = new Set(['if', 'switch', 'parallel_branches', 'try_catch', 'loop']);

export default function StepView({ nodes, edges, onNodeClick, nodeStatus, selectedNodeId }) {
  const ordered = useMemo(() => topoOrder(nodes, edges), [nodes, edges]);

  const childMap = useMemo(() => {
    const map = {};
    for (const e of edges) {
      if (!map[e.source]) map[e.source] = [];
      map[e.source].push({ target: e.target, label: e.label || e.data?.label || e.sourceHandle });
    }
    return map;
  }, [edges]);

  const nodeById = useMemo(() => {
    const m = {};
    for (const n of nodes) m[n.id] = n;
    return m;
  }, [nodes]);

  if (!ordered.length) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-tertiary)', fontSize: '14px' }}>
        No steps yet. Switch to Canvas view to add nodes.
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '24px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', background: 'var(--bg-primary)' }}>
      <div style={{ width: '100%', maxWidth: '560px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
        {ordered.map((node, idx) => {
          const type = node.data?.type || node.type;
          const isBranching = BRANCHING_TYPES.has(type);
          const icon = getIcon(node);
          const typeLabel = getNodeTypeLabel(node);
          const label = node.data?.label || typeLabel;
          const summary = summariseParams(node);
          const status = nodeStatus?.[node.id];
          const isSelected = node.id === selectedNodeId;
          const children = childMap[node.id] || [];
          const isLast = idx === ordered.length - 1;

          let statusDot = null;
          if (status === 'running') statusDot = { bg: 'var(--info-color)', label: 'Running' };
          else if (status === 'completed') statusDot = { bg: 'var(--success-color)', label: 'Done' };
          else if (status === 'failed') statusDot = { bg: 'var(--error-color)', label: 'Failed' };
          else if (status === 'skipped') statusDot = { bg: 'var(--text-tertiary)', label: 'Skipped' };

          return (
            <React.Fragment key={node.id}>
              {/* Vertical connector */}
              {idx > 0 && (
                <div style={{ width: '2px', height: '20px', background: 'var(--border-color)', flexShrink: 0 }} />
              )}

              {/* Node card */}
              <div
                onClick={() => onNodeClick(node)}
                title="Click to configure"
                style={{
                  width: '100%',
                  background: isSelected ? 'var(--bg-secondary)' : 'var(--bg-node)',
                  border: `1px solid ${isSelected ? 'var(--accent-color)' : 'var(--border-node)'}`,
                  borderRadius: '10px',
                  padding: '12px 16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  boxShadow: isSelected ? '0 0 0 2px var(--accent-color)33' : 'var(--shadow-color)',
                  transition: 'border-color 0.15s',
                  position: 'relative',
                  boxSizing: 'border-box',
                }}
                onMouseOver={e => { if (!isSelected) e.currentTarget.style.borderColor = 'var(--accent-color)'; }}
                onMouseOut={e => { if (!isSelected) e.currentTarget.style.borderColor = 'var(--border-node)'; }}
              >
                {/* Icon */}
                <div style={{ fontSize: '22px', lineHeight: 1, flexShrink: 0, marginTop: '1px', width: '28px', textAlign: 'center' }}>
                  {icon}
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {label}
                    </span>
                    <span style={{ fontSize: '10px', color: 'var(--text-tertiary)', flexShrink: 0, padding: '1px 6px', background: 'var(--bg-tertiary)', borderRadius: '10px', fontWeight: 500 }}>
                      {typeLabel}
                    </span>
                  </div>
                  {summary && (
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {summary}
                    </div>
                  )}
                  {isBranching && children.length > 0 && (
                    <div style={{ marginTop: '6px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {children.map((ch, ci) => (
                        <span key={ci} style={{ fontSize: '10px', padding: '1px 7px', borderRadius: '10px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}>
                          {ch.label || `Branch ${ci + 1}`} → {nodeById[ch.target]?.data?.label || nodeById[ch.target]?.data?.type || ch.target}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Status dot */}
                {statusDot && (
                  <div title={statusDot.label} style={{ width: '8px', height: '8px', borderRadius: '50%', background: statusDot.bg, flexShrink: 0, alignSelf: 'center' }} />
                )}

                {/* Step number */}
                <div style={{ position: 'absolute', top: '-9px', left: '14px', fontSize: '10px', fontWeight: 700, color: 'var(--text-tertiary)', background: 'var(--bg-primary)', padding: '0 4px', lineHeight: '18px' }}>
                  {idx + 1}
                </div>
              </div>

              {/* End cap */}
              {isLast && (
                <>
                  <div style={{ width: '2px', height: '20px', background: 'var(--border-color)', flexShrink: 0 }} />
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'var(--border-color)', flexShrink: 0 }} />
                </>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
